/**
 * Capture a supplier invoice — `/finance/supplier-invoices/new`.
 *
 * Capture is one transaction: the tax engine calculates, the per-line tax
 * snapshot is persisted, the branch legal series number is allocated and the
 * three-way match runs. The response already carries `status` (`matched` or
 * `discrepancy`) and every finding, so this screen hands straight off to the
 * detail page rather than pretending the verdict is still pending.
 *
 * Two contract rules are enforced in the form rather than discovered as a 422:
 * a line is either purchase-order linked or expense coded and never both, and
 * omitting the receipt on a procurement line raises a `missing_receipt`
 * finding rather than being ignored — so the receipt picker is offered right
 * next to the purchase-order line picker and its absence is called out.
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@erp/ui";
import { Plus, Trash2 } from "lucide-react";

import { surfaceProblem } from "@/components/form";
import { useIdempotentMutation } from "@/features/shared/ops-api";
import { useCurrentBranch } from "@/lib/branch/branch-store";

import {
  captureSupplierInvoice,
  type SupplierInvoice,
  type SupplierInvoiceCreate,
  type SupplierInvoiceCreateLine,
} from "../api";
import {
  PRICE_SCALE,
  QUANTITY_SCALE,
  normalizeDecimal,
  sumDecimals,
  multiplyDecimals,
  todayIsoDate,
} from "../decimal";
import {
  DateField,
  DecimalField,
  NoticeBanner,
  PageHeader,
  ProblemBanner,
  SelectField,
  TextField,
} from "../components/fields";
import {
  financeKeys,
  useGrnLinesForPurchaseOrder,
  usePartyOptions,
  usePostableAccounts,
  usePurchaseOrder,
  usePurchaseOrderOptions,
  useTaxCodeList,
} from "../queries";

const NONE = "__none__";

type LineCoding = "expense" | "purchase_order";

interface LineDraft {
  key: string;
  coding: LineCoding;
  description: string;
  quantity: string;
  unitPrice: string;
  expenseAccountId: string;
  purchaseOrderLineId: string;
  grnLineId: string;
  taxCodeId: string;
}

let lineSeq = 0;
function newLine(coding: LineCoding = "expense"): LineDraft {
  lineSeq += 1;
  return {
    key: `line-${lineSeq}`,
    coding,
    description: "",
    quantity: "",
    unitPrice: "",
    expenseAccountId: NONE,
    purchaseOrderLineId: NONE,
    grnLineId: NONE,
    taxCodeId: NONE,
  };
}

export interface SupplierInvoiceNewPageProps {
  onCaptured: (supplierInvoiceId: string) => void;
  onCancel: () => void;
  canReadParties?: boolean;
  canReadAccounts?: boolean;
  canReadTaxCodes?: boolean;
  canReadPurchasing?: boolean;
}

export function SupplierInvoiceNewPage({
  onCaptured,
  onCancel,
  canReadParties = true,
  canReadAccounts = true,
  canReadTaxCodes = true,
  canReadPurchasing = true,
}: SupplierInvoiceNewPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { branchId } = useCurrentBranch();

  const [partyId, setPartyId] = useState(NONE);
  const [supplierNumber, setSupplierNumber] = useState("");
  const [currency, setCurrency] = useState("SAR");
  const [fxRate, setFxRate] = useState("1");
  const [invoiceDate, setInvoiceDate] = useState(todayIsoDate);
  const [dueDate, setDueDate] = useState(todayIsoDate);
  const [purchaseOrderId, setPurchaseOrderId] = useState(NONE);
  const [discountPercent, setDiscountPercent] = useState("");
  const [discountDays, setDiscountDays] = useState("");
  const [lines, setLines] = useState<LineDraft[]>(() => [newLine()]);
  const [formError, setFormError] = useState<string | null>(null);

  const partiesQuery = usePartyOptions("supplier", canReadParties);
  const accountsQuery = usePostableAccounts(canReadAccounts);
  const taxCodesQuery = useTaxCodeList({ active: true }, 200);
  const purchaseOrdersQuery = usePurchaseOrderOptions(
    partyId === NONE ? null : partyId,
    canReadPurchasing,
  );
  const purchaseOrderQuery = usePurchaseOrder(
    purchaseOrderId === NONE ? null : purchaseOrderId,
    canReadPurchasing,
  );
  const grnsQuery = useGrnLinesForPurchaseOrder(
    purchaseOrderId === NONE ? null : purchaseOrderId,
    canReadPurchasing,
  );

  const partyOptions = useMemo(
    () => [
      { value: NONE, label: t("finance.common.choose", "Choose…") },
      ...(partiesQuery.data ?? []).map((party) => ({
        value: party.id,
        label: party.display_name,
      })),
    ],
    [partiesQuery.data, t],
  );

  const accountOptions = useMemo(
    () => [
      { value: NONE, label: t("finance.common.choose", "Choose…") },
      ...(accountsQuery.data ?? [])
        .filter((account) => account.is_postable)
        .map((account) => ({ value: account.id, label: `${account.code} · ${account.name}` })),
    ],
    [accountsQuery.data, t],
  );

  const taxCodeOptions = useMemo(
    () => [
      {
        value: NONE,
        label: t("finance.supplierInvoices.taxDetermined", "Determined by the engine"),
      },
      ...taxCodesQuery.rows.map((code) => ({
        value: code.id,
        label: `${code.code} · ${code.name}`,
      })),
    ],
    [taxCodesQuery.rows, t],
  );

  const purchaseOrderOptions = useMemo(
    () => [
      { value: NONE, label: t("finance.supplierInvoices.noPurchaseOrder", "No purchase order") },
      ...(purchaseOrdersQuery.data ?? []).map((order) => ({
        value: order.id,
        label: `${order.po_number} · ${order.order_date}`,
      })),
    ],
    [purchaseOrdersQuery.data, t],
  );

  const purchaseOrderLineOptions = useMemo(
    () => [
      { value: NONE, label: t("finance.common.choose", "Choose…") },
      ...(purchaseOrderQuery.data?.lines ?? []).map((line) => ({
        value: line.id,
        label: `#${line.line_number} · ${line.supplier_sku} · ${line.qty_ordered}`,
      })),
    ],
    [purchaseOrderQuery.data, t],
  );

  /** Receipt lines for a chosen purchase-order line, across every GRN. */
  const grnLineOptionsFor = (purchaseOrderLineId: string) => {
    const options = [
      {
        value: NONE,
        label: t("finance.supplierInvoices.noReceipt", "No receipt (raises a finding)"),
      },
    ];
    for (const grn of grnsQuery.data ?? []) {
      for (const line of grn.lines) {
        if (purchaseOrderLineId !== NONE && line.purchase_order_line_id !== purchaseOrderLineId) {
          continue;
        }
        options.push({
          value: line.id,
          label: `${grn.grn_number} · #${line.line_number} · ${line.qty_accepted}`,
        });
      }
    }
    return options;
  };

  const updateLine = (key: string, patch: Partial<LineDraft>) => {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  const captureMutation = useIdempotentMutation<SupplierInvoice, SupplierInvoiceCreate>({
    mutationFn: (body, key) => captureSupplierInvoice(body, key),
    onSuccess: (invoice) => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.supplierInvoices.all });
      onCaptured(invoice.id);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  /** Preview only — the server owns the authoritative net and its tax. */
  const netPreview = useMemo(() => {
    const amounts: string[] = [];
    for (const line of lines) {
      const quantity = normalizeDecimal(line.quantity, { scale: QUANTITY_SCALE });
      const unitPrice = normalizeDecimal(line.unitPrice, {
        scale: PRICE_SCALE,
        allowNegative: true,
      });
      if (quantity === null || unitPrice === null) continue;
      amounts.push(multiplyDecimals(quantity, unitPrice));
    }
    return sumDecimals(amounts);
  }, [lines]);

  const submit = () => {
    if (branchId === null) {
      setFormError(
        t(
          "finance.common.branchRequired",
          "Select an acting branch before creating an operational document.",
        ),
      );
      return;
    }
    if (partyId === NONE) {
      setFormError(t("finance.supplierInvoices.supplierRequired", "Choose the supplier."));
      return;
    }
    if (supplierNumber.trim() === "") {
      setFormError(
        t(
          "finance.supplierInvoices.numberRequired",
          "Enter the supplier's own invoice number — it is what duplicates are checked against.",
        ),
      );
      return;
    }
    if (dueDate < invoiceDate) {
      setFormError(
        t(
          "finance.supplierInvoices.dueBeforeInvoice",
          "The due date cannot precede the invoice date.",
        ),
      );
      return;
    }

    const wireLines: SupplierInvoiceCreateLine[] = [];
    for (const [index, line] of lines.entries()) {
      const position = index + 1;
      if (line.description.trim() === "") {
        setFormError(
          t(
            "finance.supplierInvoices.lineDescriptionRequired",
            "Line {{line}} needs a description.",
            {
              line: position,
            },
          ),
        );
        return;
      }
      const quantity = normalizeDecimal(line.quantity, {
        scale: QUANTITY_SCALE,
        allowZero: false,
      });
      if (quantity === null) {
        setFormError(
          t(
            "finance.supplierInvoices.lineQuantityInvalid",
            "Line {{line}} needs a non-zero quantity.",
            {
              line: position,
            },
          ),
        );
        return;
      }
      const unitPrice = normalizeDecimal(line.unitPrice, {
        scale: PRICE_SCALE,
        allowNegative: true,
      });
      if (unitPrice === null) {
        setFormError(
          t("finance.supplierInvoices.linePriceInvalid", "Line {{line}} needs a unit price.", {
            line: position,
          }),
        );
        return;
      }
      if (line.coding === "expense" && line.expenseAccountId === NONE) {
        setFormError(
          t(
            "finance.supplierInvoices.lineAccountRequired",
            "Line {{line}} needs an expense account.",
            {
              line: position,
            },
          ),
        );
        return;
      }
      if (line.coding === "purchase_order" && line.purchaseOrderLineId === NONE) {
        setFormError(
          t(
            "finance.supplierInvoices.linePoLineRequired",
            "Line {{line}} needs the purchase-order line it invoices.",
            { line: position },
          ),
        );
        return;
      }
      wireLines.push({
        description: line.description.trim(),
        quantity,
        unit_price: unitPrice,
        ...(line.coding === "expense"
          ? { expense_account_id: line.expenseAccountId }
          : {
              purchase_order_line_id: line.purchaseOrderLineId,
              ...(line.grnLineId === NONE ? {} : { grn_line_id: line.grnLineId }),
            }),
        ...(line.taxCodeId === NONE ? {} : { tax_code_id: line.taxCodeId }),
      });
    }

    const fx = normalizeDecimal(fxRate, { scale: 10, allowZero: false });
    if (fx === null) {
      setFormError(t("finance.common.fxRateInvalid", "Enter a positive exchange rate."));
      return;
    }
    const discount =
      discountPercent.trim() === "" ? null : normalizeDecimal(discountPercent, { scale: 6 });
    if (discountPercent.trim() !== "" && discount === null) {
      setFormError(
        t(
          "finance.supplierInvoices.discountInvalid",
          "Enter the early-settlement discount as a percentage.",
        ),
      );
      return;
    }
    const days = discountDays.trim() === "" ? null : Number.parseInt(discountDays, 10);
    if (days !== null && (Number.isNaN(days) || days < 0 || days > 365)) {
      setFormError(
        t("finance.supplierInvoices.discountDaysInvalid", "The discount window is 0 to 365 days."),
      );
      return;
    }

    setFormError(null);
    captureMutation.mutate({
      branch_id: branchId,
      party_id: partyId,
      supplier_invoice_number: supplierNumber.trim(),
      currency,
      fx_rate: fx,
      invoice_date: invoiceDate,
      due_date: dueDate,
      ...(purchaseOrderId === NONE ? {} : { purchase_order_id: purchaseOrderId }),
      ...(discount === null ? {} : { early_discount_percent: discount }),
      ...(days === null ? {} : { early_discount_days: days }),
      lines: wireLines,
    });
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={t("finance.supplierInvoices.captureTitle", "Capture supplier invoice")}
        description={t(
          "finance.supplierInvoices.captureSubtitle",
          "Tax is calculated and the three-way match runs in the same transaction as the capture.",
        )}
        actions={
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t("actions.cancel")}
          </Button>
        }
      />

      {branchId === null ? (
        <NoticeBanner>
          {t(
            "finance.common.branchRequired",
            "Select an acting branch before creating an operational document.",
          )}
        </NoticeBanner>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("finance.supplierInvoices.headerSection", "Invoice")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <SelectField
            id="si-party"
            label={t("finance.supplierInvoices.supplier", "Supplier")}
            value={partyId}
            onValueChange={(value) => {
              setPartyId(value);
              setPurchaseOrderId(NONE);
            }}
            options={partyOptions}
            disabled={!canReadParties}
          />
          <TextField
            id="si-number"
            label={t("finance.supplierInvoices.supplierNumber", "Supplier invoice number")}
            value={supplierNumber}
            onChange={setSupplierNumber}
            hint={t(
              "finance.supplierInvoices.supplierNumberHint",
              "Guarded per supplier, case-insensitively and ignoring punctuation.",
            )}
            ltr
            maxLength={64}
          />
          <TextField
            id="si-currency"
            label={t("finance.common.currency", "Currency")}
            value={currency}
            onChange={(value) => setCurrency(value.toUpperCase())}
            ltr
            maxLength={3}
          />
          <DecimalField
            id="si-fx"
            label={t("finance.common.fxRate", "Exchange rate")}
            value={fxRate}
            onChange={setFxRate}
            decimal={{ scale: 10, allowZero: false }}
            hint={t("finance.common.fxRateHint", "Base-currency units per document-currency unit.")}
            invalidMessage={t("finance.common.fxRateInvalid", "Enter a positive exchange rate.")}
          />
          <DateField
            id="si-invoice-date"
            label={t("finance.supplierInvoices.invoiceDate", "Invoice date")}
            value={invoiceDate}
            onChange={setInvoiceDate}
          />
          <DateField
            id="si-due-date"
            label={t("finance.supplierInvoices.dueDate", "Due date")}
            value={dueDate}
            onChange={setDueDate}
          />
          <SelectField
            id="si-purchase-order"
            label={t("finance.supplierInvoices.purchaseOrder", "Purchase order")}
            value={purchaseOrderId}
            onValueChange={setPurchaseOrderId}
            options={purchaseOrderOptions}
            disabled={!canReadPurchasing || partyId === NONE}
            hint={t(
              "finance.supplierInvoices.purchaseOrderHint",
              "Pick one to code lines against its lines and receipts.",
            )}
          />
          <DecimalField
            id="si-discount-percent"
            label={t("finance.supplierInvoices.discountPercent", "Early settlement discount %")}
            value={discountPercent}
            onChange={setDiscountPercent}
            decimal={{ scale: 6 }}
            invalidMessage={t(
              "finance.supplierInvoices.discountInvalid",
              "Enter the early-settlement discount as a percentage.",
            )}
          />
          <TextField
            id="si-discount-days"
            label={t("finance.supplierInvoices.discountDays", "Discount window (days)")}
            value={discountDays}
            onChange={setDiscountDays}
            ltr
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3">
          <CardTitle>
            <h2>{t("finance.supplierInvoices.linesSection", "Lines")}</h2>
          </CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setLines((current) => [...current, newLine()])}
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("finance.supplierInvoices.addLine", "Add line")}
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          {lines.map((line, index) => (
            <div key={line.key} className="flex flex-col gap-3 border-b pb-4 last:border-b-0">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-medium">
                  {t("finance.supplierInvoices.lineHeading", "Line {{line}}", { line: index + 1 })}
                </h3>
                {lines.length > 1 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t("finance.supplierInvoices.removeLine", "Remove line {{line}}", {
                      line: index + 1,
                    })}
                    onClick={() =>
                      setLines((current) =>
                        current.filter((candidate) => candidate.key !== line.key),
                      )
                    }
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </Button>
                ) : null}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <SelectField
                  id={`si-coding-${line.key}`}
                  label={t("finance.supplierInvoices.lineCoding", "Coding")}
                  value={line.coding}
                  onValueChange={(coding) =>
                    updateLine(line.key, {
                      coding: coding as LineCoding,
                      expenseAccountId: NONE,
                      purchaseOrderLineId: NONE,
                      grnLineId: NONE,
                    })
                  }
                  options={[
                    {
                      value: "expense",
                      label: t("finance.supplierInvoices.codingExpense", "Expense account"),
                    },
                    {
                      value: "purchase_order",
                      label: t("finance.supplierInvoices.codingPo", "Purchase-order line"),
                    },
                  ]}
                  hint={t(
                    "finance.supplierInvoices.codingHint",
                    "A line is one or the other, never both.",
                  )}
                />
                <TextField
                  id={`si-description-${line.key}`}
                  label={t("finance.supplierInvoices.lineDescription", "Description")}
                  value={line.description}
                  onChange={(description) => updateLine(line.key, { description })}
                  className="lg:col-span-3"
                  maxLength={500}
                />
                <DecimalField
                  id={`si-quantity-${line.key}`}
                  label={t("finance.supplierInvoices.lineQuantity", "Quantity")}
                  value={line.quantity}
                  onChange={(quantity) => updateLine(line.key, { quantity })}
                  decimal={{ scale: QUANTITY_SCALE, allowZero: false }}
                  invalidMessage={t(
                    "finance.supplierInvoices.quantityInvalid",
                    "Quantities carry at most six decimals and cannot be zero.",
                  )}
                />
                <DecimalField
                  id={`si-unit-price-${line.key}`}
                  label={t("finance.supplierInvoices.lineUnitPrice", "Unit price")}
                  value={line.unitPrice}
                  onChange={(unitPrice) => updateLine(line.key, { unitPrice })}
                  decimal={{ scale: PRICE_SCALE, allowNegative: true }}
                  invalidMessage={t(
                    "finance.supplierInvoices.priceInvalid",
                    "Prices carry at most six decimals.",
                  )}
                />
                {line.coding === "expense" ? (
                  <SelectField
                    id={`si-account-${line.key}`}
                    label={t("finance.supplierInvoices.lineExpenseAccount", "Expense account")}
                    value={line.expenseAccountId}
                    onValueChange={(expenseAccountId) => updateLine(line.key, { expenseAccountId })}
                    options={accountOptions}
                    disabled={!canReadAccounts}
                    className="lg:col-span-2"
                  />
                ) : (
                  <>
                    <SelectField
                      id={`si-po-line-${line.key}`}
                      label={t("finance.supplierInvoices.linePoLine", "Purchase-order line")}
                      value={line.purchaseOrderLineId}
                      onValueChange={(purchaseOrderLineId) =>
                        updateLine(line.key, { purchaseOrderLineId, grnLineId: NONE })
                      }
                      options={purchaseOrderLineOptions}
                      disabled={!canReadPurchasing || purchaseOrderId === NONE}
                    />
                    <SelectField
                      id={`si-grn-line-${line.key}`}
                      label={t("finance.supplierInvoices.lineGrnLine", "Goods receipt line")}
                      value={line.grnLineId}
                      onValueChange={(grnLineId) => updateLine(line.key, { grnLineId })}
                      options={grnLineOptionsFor(line.purchaseOrderLineId)}
                      disabled={!canReadPurchasing || purchaseOrderId === NONE}
                      hint={t(
                        "finance.supplierInvoices.grnLineHint",
                        "Leaving this empty raises a missing_receipt finding.",
                      )}
                    />
                  </>
                )}
                <SelectField
                  id={`si-tax-code-${line.key}`}
                  label={t("finance.supplierInvoices.lineTaxCode", "Tax code")}
                  value={line.taxCodeId}
                  onValueChange={(taxCodeId) => updateLine(line.key, { taxCodeId })}
                  options={taxCodeOptions}
                  disabled={!canReadTaxCodes}
                  hint={t(
                    "finance.supplierInvoices.taxCodeHint",
                    "Pin a code, or leave it for the tax engine to determine.",
                  )}
                />
              </div>
            </div>
          ))}

          <p className="text-sm text-muted-foreground" data-testid="supplier-invoice-net-preview">
            {t(
              "finance.supplierInvoices.netPreview",
              "Net of entered lines: {{amount}} {{currency}}",
              {
                amount: netPreview,
                currency,
              },
            )}
          </p>

          {formError === null ? null : <ProblemBanner>{formError}</ProblemBanner>}

          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" loading={captureMutation.isPending} onClick={submit}>
              {t("finance.supplierInvoices.captureAction", "Capture and match")}
            </Button>
            <Button type="button" variant="ghost" onClick={onCancel}>
              {t("actions.cancel")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
